/**
 * 中间件工具函数：登录状态检查
 * 用于在 Next.js 中间件中检查用户登录状态
 */

interface HTTPProtocol<T = unknown> {
  code: number
  data: T | null
  msg?: string
  error_desc?: string
}

interface LoginError {
  code: number
  msg: string
}

/**
 * 检查登录状态
 * @param authToken - 用户的认证 token
 * @param apiHost - API 服务器地址
 * @param language - 语言代码（可选）
 * @returns 如果登录失效，返回错误信息；否则返回 null
 */
export async function checkLoginStatus(
  authToken: string | undefined,
  apiHost: string,
  language?: string
): Promise<LoginError | null> {
  // 如果没有 token，不需要检查（未登录状态）
  if (!authToken) {
    return null
  }

  try {
    // 构建 URL，添加 auth_token 和 language 查询参数
    const url = new URL(`${apiHost}/usercenter/check_user_in_use`)
    url.searchParams.set("auth_token", authToken)

    url.searchParams.set("language", language || "en")

    // 构建 headers
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
    }

    headers["Language"] = language || "en"

    // 打印请求的完整链接
    console.log("[checkLoginStatus] Request URL:", url.toString())
    // 创建兼容的 timeout 实现 (支持 Safari 12.1+)
    // AbortSignal.timeout() 仅在 Safari 16.4+ 支持,使用 AbortController 提供更好的兼容性
    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), 3000)

    try {
      // 调用登录状态检查接口
      const response = await fetch(url.toString(), {
        method: "GET",
        headers,
        // 设置较短的超时时间，避免阻塞页面加载
        signal: controller.signal,
      })

      clearTimeout(timeoutId)

      if (!response.ok) {
        // 网络错误不应阻断页面访问
        console.warn("[checkLoginStatus] HTTP error:", response.status)
        return null
      }

      const result = (await response.json()) as HTTPProtocol<null>
      console.warn("[checkLoginStatus] result:", result)
      // 如果 code !== 0，说明登录失效
      if (result.code !== 0) {
        return {
          code: result.code,
          msg: result.msg || result.error_desc || "Login expired",
        }
      }

      // 登录状态正常
      return null
    } catch (error) {
      clearTimeout(timeoutId)
      // 捕获所有错误（网络错误、超时等），不阻断页面访问
      console.warn("[checkLoginStatus] Error:", error instanceof Error ? error.message : error)
      return null
    }
  } catch (error) {
    // 外层 catch 处理其他意外错误
    console.warn("[checkLoginStatus] Unexpected error:", error instanceof Error ? error.message : error)
    return null
  }
}
